import { useMemo, useState } from "react";
import { Sparkles, Zap, ArrowUpRight, X, Crown, Check } from "lucide-react";

/**
 * RendersChip — luxury quota indicator + upgrade nudge.
 * - Shows N / limit + a slim progress bar with colour states (healthy / warning / critical).
 * - Click opens a polished upgrade dialog with current plan + paid tier preview.
 * - Free plan only for now; "Notify me" CTA captures interest in the paid plan.
 */
export default function RendersChip({ quota, onTopup }) {
  const [open, setOpen] = useState(false);
  const { used = 0, limit = 10, remaining = 0, credits, plan } = quota || {};

  // Credits-based display (new model). `remaining` and `credits` track the same value.
  const isCreditsModel = typeof credits === "number";
  const isEdu = plan === "edu";

  const pct = Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const state = useMemo(() => {
    if (remaining <= 0) return "critical";
    if (remaining <= 5) return "warning";
    return "healthy";
  }, [remaining]);

  const palette = {
    healthy:  { bar: "bg-[#D4AF37]", text: "text-[#D4AF37]", ring: "border-[#D4AF37]/35" },
    warning:  { bar: "bg-[#E8A93D]", text: "text-[#E8A93D]", ring: "border-[#E8A93D]/45" },
    critical: { bar: "bg-red-400",   text: "text-red-400",   ring: "border-red-400/45" },
  }[state];

  return (
    <>
      <button
        type="button"
        data-testid="renders-chip-btn"
        onClick={() => setOpen(true)}
        className={`hidden sm:flex items-center gap-2.5 text-xs pl-3 pr-2.5 py-1.5 rounded-full border bg-[#141414] hover:bg-[#1A1A1A] transition group ${palette.ring}`}
        title={isCreditsModel ? "Wallet · plan & credits" : "View plan & quota"}
      >
        <Sparkles className={`w-3 h-3 ${palette.text}`} />
        {isCreditsModel ? (
          <>
            <span className="text-white/65">Credits</span>
            <span className={palette.text}>{Number(credits).toFixed(credits % 1 === 0 ? 0 : 1)}</span>
            {isEdu && (
              <span className="ml-1 text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full border border-[#7DD3FC]/40 text-[#7DD3FC]">
                Edu
              </span>
            )}
          </>
        ) : (
          <>
            <span className="text-white/65">Renders</span>
            <span className={palette.text}>
              {used}/{limit}
            </span>
            <div className="w-12 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div
                className={`h-full ${palette.bar} transition-all duration-500`}
                style={{ width: `${pct}%` }}
                data-testid="renders-chip-bar"
              />
            </div>
          </>
        )}
        <ArrowUpRight className="w-3 h-3 text-white/30 group-hover:text-[#D4AF37] transition" />
      </button>

      {open && (
        <UpgradeDialog
          quota={{ used, limit, remaining, pct, credits }}
          state={state}
          isCreditsModel={isCreditsModel}
          onClose={() => setOpen(false)}
          onTopup={() => { setOpen(false); onTopup?.(); }}
        />
      )}
    </>
  );
}

function UpgradeDialog({ quota, state, isCreditsModel, onClose, onTopup }) {
  const headline = isCreditsModel
    ? "Your credit wallet"
    : state === "critical"
      ? "You're out of renders for this month."
      : state === "warning"
        ? `Just ${quota.remaining} renders left.`
        : "Designing comfortably.";
  const sub = isCreditsModel
    ? "Top up anytime — credits never expire."
    : state === "critical"
      ? "Your quota resets on the 1st."
      : state === "warning"
        ? "Keep iterating — or top up your wallet for headroom."
        : "Credits power every render, refine, multiview & CAD export.";

  return (
    <div
      role="dialog"
      data-testid="upgrade-dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl bg-[#0E0E0E] border border-white/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] p-7 sm:p-9"
      >
        <button
          onClick={onClose}
          data-testid="upgrade-dialog-close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full border border-white/10 text-white/55 hover:text-white hover:bg-white/5 flex items-center justify-center transition"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
          <Crown className="w-3 h-3" /> Wallet
        </div>
        <h2 className="font-display text-2xl sm:text-3xl tracking-tight text-white mt-1.5">
          {headline}
        </h2>
        <p className="text-sm text-white/55 mt-1.5">{sub}</p>

        {/* Current wallet card */}
        <div className="mt-6 rounded-xl border border-white/10 bg-[#141414] p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">Available</div>
              <div className="font-display text-3xl text-[#D4AF37] mt-0.5">
                {isCreditsModel
                  ? Number(quota.credits ?? 0).toFixed(quota.credits % 1 === 0 ? 0 : 1)
                  : quota.remaining}
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mt-0.5">
                {isCreditsModel ? "credits" : "renders"}
              </div>
            </div>
            {!isCreditsModel && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">used / limit</div>
                <div className="font-display text-xl text-white mt-0.5">
                  {quota.used} / {quota.limit}
                </div>
              </div>
            )}
          </div>
          {!isCreditsModel && (
            <>
              <div className="w-full h-2 rounded-full bg-[#0B0B0B] overflow-hidden">
                <div
                  className={`h-full transition-all duration-700 ${
                    state === "critical" ? "bg-red-400" : state === "warning" ? "bg-[#E8A93D]" : "bg-[#D4AF37]"
                  }`}
                  style={{ width: `${quota.pct}%` }}
                />
              </div>
              <div className="mt-2 text-[11px] text-white/45">resets on the 1st</div>
            </>
          )}
        </div>

        {/* Top up CTA */}
        {onTopup && (
          <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
            <button
              onClick={onTopup}
              data-testid="upgrade-topup-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-3 hover:bg-[#F3CE5A] transition"
            >
              <Zap className="w-4 h-4" /> Top up credits
            </button>
            <button
              onClick={onClose}
              className="text-sm px-5 py-3 rounded-full border border-white/15 text-white/75 hover:bg-white/5 transition"
            >
              Keep designing
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
